import React from "react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { COUNTRIES, type Country } from "../constants/countries";
import { useTelegramSendOtp, useTelegramVerifyOtp } from "../hooks";

import { CountrySelector } from "./CountrySelector";

interface TelegramFormData {
  phoneNumber: string;
  otp: string;
  password: string;
  selectedCountry: Country;
}

interface TelegramFormProps {
  onSuccess?: (phoneNumber: string) => void;
}

export function TelegramForm({ onSuccess }: TelegramFormProps) {
  const [formData, setFormData] = React.useState<TelegramFormData>({
    phoneNumber: "",
    otp: "",
    password: "",
    selectedCountry:
      COUNTRIES.find((country) => country.code === "IN") || COUNTRIES[0],
  });

  const [errors, setErrors] = React.useState<Partial<TelegramFormData>>({});
  const [otpSent, setOtpSent] = React.useState(false);

  const {
    mutate: sendOtp,
    isPending: isSendingOtp,
    error: otpError,
  } = useTelegramSendOtp();

  const {
    mutate: verifyOtp,
    isPending: isVerifying,
    error: verifyError,
  } = useTelegramVerifyOtp();

  const validatePhoneNumber = () => {
    const newErrors: Partial<TelegramFormData> = {};

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{7,14}$/.test(formData.phoneNumber.replace(/\s/g, ""))) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors: Partial<TelegramFormData> = {};

    if (!formData.otp.trim()) {
      newErrors.otp = "OTP is required";
    } else if (!/^\d{5}$/.test(formData.otp)) {
      newErrors.otp = "Please enter a valid 5-digit OTP";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof TelegramFormData, value: string) => {
    if (field === "otp") {
      const numericValue = value.replace(/\D/g, "").slice(0, 5);
      setFormData((prev) => ({ ...prev, [field]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCountryChange = (countryCode: string) => {
    const selectedCountry = COUNTRIES.find(
      (country) => country.code === countryCode,
    );
    if (selectedCountry) {
      setFormData((prev) => ({ ...prev, selectedCountry }));
    }
  };

  const handleGenerateOtp = async () => {
    if (validatePhoneNumber()) {
      const fullPhoneNumber =
        formData.selectedCountry.prefix + formData.phoneNumber;
      sendOtp(
        { phone_number: fullPhoneNumber },
        {
          onSuccess: () => {
            setOtpSent(true);
          },
          onError: (error: Error) => {
            console.error("Failed to send OTP:", error);
          },
        },
      );
    }
  };

  const handleResendOtp = async () => {
    const fullPhoneNumber =
      formData.selectedCountry.prefix + formData.phoneNumber;
    sendOtp(
      { phone_number: fullPhoneNumber },
      {
        onSuccess: () => {
          setOtpSent(true);
        },
        onError: (error: Error) => {
          console.error("Failed to resend OTP:", error);
        },
      },
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateOtp()) {
      const fullPhoneNumber =
        formData.selectedCountry.prefix + formData.phoneNumber;
      const verifyData: {
        phone_number: string;
        otp: number;
        password?: string;
      } = {
        phone_number: fullPhoneNumber,
        otp: parseInt(formData.otp, 10),
      };

      if (formData.password.trim()) {
        verifyData.password = formData.password;
      }

      verifyOtp(verifyData, {
        onSuccess: () => {
          onSuccess?.(fullPhoneNumber);
        },
        onError: (error: Error) => {
          console.error("Telegram verification failed:", error);
        },
      });
    }
  };

  const isPhoneValid = formData.phoneNumber.trim() && !errors.phoneNumber;
  const isOtpValid = formData.otp.trim() && !errors.otp;
  const isLoading = isSendingOtp || isVerifying;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1">
        <Label
          htmlFor="phoneNumber"
          className={`text-base font-medium ${
            otpSent
              ? "text-zinc-500 dark:text-zinc-400"
              : "text-black dark:text-white"
          }`}
        >
          Phone number
        </Label>
        <div className="flex gap-2 items-center">
          <CountrySelector
            selectedCountry={formData.selectedCountry}
            onCountryChange={handleCountryChange}
            disabled={isLoading || otpSent}
            className={otpSent ? "opacity-50" : ""}
          />
          <Input
            id="phoneNumber"
            type="tel"
            placeholder="Enter your phone number"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
            className={`flex-1 ${otpSent ? "opacity-50" : ""}`}
            disabled={isLoading || otpSent}
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleGenerateOtp}
            disabled={!isPhoneValid || isLoading || otpSent}
            className={`shrink-0 ${otpSent ? "opacity-50" : ""}`}
          >
            {isSendingOtp ? "Sending..." : "Generate OTP"}
          </Button>
        </div>
        {errors.phoneNumber && (
          <p className="text-sm text-red-500">{errors.phoneNumber}</p>
        )}
        {otpSent && (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            OTP has been sent
          </p>
        )}
      </div>

      {/* OTP Field */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label
            htmlFor="otp"
            className={`text-base font-medium ${
              otpSent
                ? "text-black dark:text-white"
                : "text-zinc-500 dark:text-zinc-400"
            }`}
          >
            OTP
          </Label>
          <Button
            type="button"
            variant="link"
            onClick={handleResendOtp}
            className="h-auto p-0 text-sm underline hover:no-underline"
            disabled={!otpSent || isLoading}
          >
            Resend OTP
          </Button>
        </div>
        <Input
          id="otp"
          type="text"
          placeholder="Enter the OTP from telegram"
          value={formData.otp}
          onChange={(e) => handleInputChange("otp", e.target.value)}
          className="w-full"
          disabled={!otpSent || isLoading}
          maxLength={5}
          inputMode="numeric"
          pattern="[0-9]*"
        />
        {errors.otp && <p className="text-sm text-red-500">{errors.otp}</p>}
      </div>

      {/* 2FA Password Field */}
      <>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 font-semibold">
          If you have 2FA on please enter your password here.
        </p>
        <div className="space-y-1">
          <Label
            htmlFor="password"
            className={`text-base font-medium ${
              otpSent
                ? "text-black dark:text-white"
                : "text-zinc-500 dark:text-zinc-400"
            }`}
          >
            Password
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="Enter your 2FA password"
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            className="w-full"
            disabled={!otpSent || isLoading}
          />
        </div>
      </>

      {(otpError || verifyError) && (
        <div className="text-sm text-red-500">
          Failed to {otpError ? "send OTP" : "verify"}:{" "}
          {(otpError || verifyError)?.message}
        </div>
      )}

      <Button
        type="submit"
        className="w-full h-10"
        disabled={!isOtpValid || isLoading}
      >
        {isVerifying ? "Verifying..." : "Continue"}
      </Button>
    </form>
  );
}

export type { TelegramFormData };
